/**
 * Build query params for payment reconciliation GET (server expects same filters as run).
 * booking_statuses: comma-separated string or array of status codes.
 */
export function buildReconciliationQueryParams({
  limit,
  created_after,
  location,
  booking_statuses,
  include_payment_led_orphans,
  payments_from_days,
  payments_list_max,
  include_razorpay_api_payloads,
  redact_razorpay_payloads,
}) {
  const params = {};
  if (limit != null && limit !== "") {
    const n = Number(limit);
    if (!Number.isNaN(n) && n > 0) params.limit = String(Math.floor(n));
  }
  if (created_after) {
    params.created_after =
      typeof created_after === "string"
        ? created_after
        : created_after.format
        ? created_after.format("YYYY-MM-DD")
        : String(created_after);
  }
  if (location) params.location = String(location).trim();
  if (booking_statuses != null && booking_statuses !== "") {
    const raw = Array.isArray(booking_statuses)
      ? booking_statuses.filter(Boolean).join(",")
      : String(booking_statuses).trim();
    if (raw) params.booking_statuses = raw;
  }

  params.include_payment_led_orphans = include_payment_led_orphans
    ? "true"
    : "false";
  const days = clampInt(payments_from_days, 1, 90, 7);
  const maxList = clampInt(payments_list_max, 1, 500, 200);
  params.payments_from_days = String(days);
  params.payments_list_max = String(maxList);

  params.include_razorpay_api_payloads = include_razorpay_api_payloads
    ? "true"
    : "false";
  const redact =
    redact_razorpay_payloads === false ? false : true;
  params.redact_razorpay_payloads = redact ? "true" : "false";

  return params;
}

/** GET booking detail — same Razorpay payload flags as report. */
export function buildBookingDetailQueryParams({
  include_razorpay_api_payloads,
  redact_razorpay_payloads,
}) {
  return {
    include_razorpay_api_payloads: include_razorpay_api_payloads
      ? "true"
      : "false",
    redact_razorpay_payloads:
      redact_razorpay_payloads === false ? "false" : "true",
  };
}

function clampInt(value, min, max, fallback) {
  const n = Number(value);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(n)));
}

/** POST /reconciliation/run body: remediate + payment-led options (same keys as query). */
export function buildReconciliationRunBody(filters) {
  const {
    include_payment_led_orphans,
    payments_from_days,
    payments_list_max,
    include_razorpay_api_payloads,
    redact_razorpay_payloads,
  } = filters || {};
  return {
    remediate: true,
    include_payment_led_orphans: Boolean(include_payment_led_orphans),
    payments_from_days: clampInt(payments_from_days, 1, 90, 7),
    payments_list_max: clampInt(payments_list_max, 1, 500, 200),
    include_razorpay_api_payloads: Boolean(include_razorpay_api_payloads),
    redact_razorpay_payloads:
      redact_razorpay_payloads === false ? false : true,
  };
}

export function appendQueryToUrl(baseUrl, params) {
  const entries = Object.entries(params || {}).filter(
    ([, v]) => v != null && v !== ""
  );
  if (entries.length === 0) return baseUrl;
  const qs = new URLSearchParams();
  entries.forEach(([k, v]) => qs.append(k, String(v)));
  return `${baseUrl}?${qs.toString()}`;
}

export const RECONCILIATION_ISSUE_KIND_LABELS = {
  MISSING_PROVIDER_PAYMENT_ID:
    "No provider payment id in DB (placeholder txn)",
  TRANSACTION_ID_MISMATCH: "DB transaction id ≠ gateway payment",
  STATUS_DRIFT: "Booking status out of sync with gateway",
  DB_PAID_GATEWAY_NOT_AUTHORIZED_OR_CAPTURED:
    "DB is paid-like but linked Razorpay payment is not authorized/captured",
  RAZORPAY_PAYMENT_NO_MATCHING_BOOKING:
    "Captured/authorized Razorpay payment has no booking with this order id",
};

export function getIssueKindLabel(issueKind) {
  if (!issueKind) return "—";
  return RECONCILIATION_ISSUE_KIND_LABELS[issueKind] || issueKind;
}

/** Razorpay column: raw → normalized → — */
export function getRazorpayPaymentStatusDisplay(issue) {
  const raw = issue?.razorpay_payment_status_raw;
  if (raw != null && String(raw).trim() !== "") return String(raw);
  const norm = issue?.razorpay_payment_status_normalized;
  if (norm != null && String(norm).trim() !== "") return String(norm);
  return "—";
}

/** INR display from minor units (paise). */
export function formatInrFromMinor(amountMinor) {
  if (amountMinor == null || Number.isNaN(Number(amountMinor))) return "—";
  const rupees = Number(amountMinor) / 100;
  return `₹${rupees.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** ISO 8601 (or API string) for support tables; never invent missing times. */
export function formatIsoForDisplay(iso) {
  if (iso == null || String(iso).trim() === "") return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "medium",
    });
  } catch {
    return String(iso);
  }
}

export function getRazorpayPrimaryPaymentTimeline(issue) {
  const t = issue?.razorpay_primary_payment_timeline;
  if (t && typeof t === "object") return t;
  return {
    steps: [],
    current_status_raw: null,
    current_status_normalized: null,
  };
}

/** Primary gateway column: timeline current status (raw preferred), else — */
export function getPrimaryGatewayStatusDisplay(issue) {
  const t = getRazorpayPrimaryPaymentTimeline(issue);
  const raw = t.current_status_raw;
  if (raw != null && String(raw).trim() !== "") return String(raw);
  const norm = t.current_status_normalized;
  if (norm != null && String(norm).trim() !== "") return String(norm);
  return "—";
}

export function getPrimaryGatewayStatusTooltip(issue) {
  const t = getRazorpayPrimaryPaymentTimeline(issue);
  const parts = [];
  if (t.current_status_raw != null && String(t.current_status_raw).trim() !== "")
    parts.push(`Raw: ${t.current_status_raw}`);
  if (
    t.current_status_normalized != null &&
    String(t.current_status_normalized).trim() !== ""
  )
    parts.push(`Normalized: ${t.current_status_normalized}`);
  return parts.length ? parts.join("\n") : "";
}

export function getTimelineSteps(issue) {
  const t = getRazorpayPrimaryPaymentTimeline(issue);
  const steps = t.steps;
  return Array.isArray(steps) ? steps : [];
}

export function formatTimelineEventLabel(event) {
  if (!event) return "—";
  const e = String(event).toLowerCase();
  const map = {
    created: "Created",
    authorized: "Authorized",
    captured: "Captured",
    failed: "Failed",
  };
  return map[e] || event;
}

/** Orphan / payment-led row: no booking id */
export function isReconciliationOrphanIssue(issue) {
  const bid = issue?.bookingId ?? issue?.booking_id;
  return bid == null || String(bid).trim() === "";
}

export function formatSlotDisplay(issue) {
  const d = issue?.slot_date;
  const t = issue?.slot_time;
  const parts = [d, t].filter((x) => x != null && String(x).trim() !== "");
  return parts.length ? parts.join(" · ") : "—";
}

/** Oldest first; null `at` last. */
export function sortLifecycleEventsAsc(events) {
  if (!Array.isArray(events)) return [];
  return [...events].sort((a, b) => {
    const ta = a?.at ? new Date(a.at).getTime() : NaN;
    const tb = b?.at ? new Date(b.at).getTime() : NaN;
    const validA = !Number.isNaN(ta);
    const validB = !Number.isNaN(tb);
    if (validA && validB) return ta - tb;
    if (validA) return -1;
    if (validB) return 1;
    return 0;
  });
}

export function normalizeBookingDetailPayload(body) {
  const root =
    body && typeof body === "object"
      ? body.response ?? body.data ?? body
      : {};
  return {
    booking_found: Boolean(root.booking_found),
    issues: Array.isArray(root.issues) ? root.issues : [],
  };
}

export function normalizeScanPayload(body) {
  const root =
    body && typeof body === "object"
      ? body.response ?? body.data ?? body
      : {};
  const summary = root.summary || {};
  const scanMeta = root.scan_meta && typeof root.scan_meta === "object"
    ? root.scan_meta
    : {};

  return {
    issues: Array.isArray(root.issues) ? root.issues : [],
    scanned_count: root.scanned_count ?? 0,
    summary: {
      missing_provider_payment_id: summary.missing_provider_payment_id ?? 0,
      status_drift: summary.status_drift ?? 0,
      transaction_id_mismatch: summary.transaction_id_mismatch ?? 0,
      db_paid_gateway_not_authorized_or_captured:
        summary.db_paid_gateway_not_authorized_or_captured ??
        summary.gateway_failed_booking_paid ??
        0,
      razorpay_payment_no_matching_booking:
        summary.razorpay_payment_no_matching_booking ?? 0,
    },
    scan_meta: {
      payment_led_enabled: Boolean(scanMeta.payment_led_enabled),
      payment_led_payments_fetched:
        scanMeta.payment_led_payments_fetched ?? null,
      payment_led_api_errors: scanMeta.payment_led_api_errors ?? null,
    },
    remediated_booking_ids: Array.isArray(root.remediated_booking_ids)
      ? root.remediated_booking_ids
      : [],
    remediation_errors: Array.isArray(root.remediation_errors)
      ? root.remediation_errors
      : [],
  };
}
